import { Dialog, DialogContent, DialogTrigger } from '@civilgrid/ui/components';
import { forwardRef, useState } from 'react';
import { useSnapshot } from 'valtio';
import { Action } from '~/authorizations';
import Can from '~/authorizations/Can';
import { EditorMode } from '~/common/types';
import Popover from '~/components2/Popover/Popover';
import { useCivilDrawStore } from '../../civilDrawStoreContext';
import { ImageImport } from './ImageImport';
import StampPicker from './StampPicker';
import {
  ArrowIcon,
  CircleIcon,
  ImageIcon,
  LineIcon,
  MarkerIcon,
  PolygonIcon,
  StampIcon,
  TextIcon,
} from './ToolbarIcons';
import { toolbarClickMode } from './toolbarClickMode';

interface ToolbarButtonProps {
  text: string;
  mode: EditorMode;
  /** Set on buttons that open a picker, so a second click reopens it instead of exiting draw mode. */
  opensPicker?: boolean;
}

const iconMap: { [key: string]: typeof PolygonIcon } = {
  [EditorMode.DrawPolygon]: PolygonIcon,
  [EditorMode.DrawLine]: LineIcon,
  [EditorMode.DrawCircle]: CircleIcon,
  [EditorMode.DrawMarker]: MarkerIcon,
  [EditorMode.DrawText]: TextIcon,
  [EditorMode.DrawTextV2]: TextIcon,
  [EditorMode.DrawImage]: ImageIcon,
  [EditorMode.DrawStamp]: StampIcon,
  [EditorMode.DrawArrow]: ArrowIcon,
};

const ToolbarButton = forwardRef<HTMLButtonElement, ToolbarButtonProps>(
  ({ text, mode, opensPicker = false, ...props }, ref) => {
    const store = useCivilDrawStore();
    const { editorMode } = useSnapshot(store) as typeof store;
    const isSelected = editorMode === mode;
    const nextMode = toolbarClickMode(editorMode, mode, opensPicker);

    const Icon = iconMap[mode];
    return (
      <Can action={Action.EditCivilDraw}>
        <button
          ref={ref}
          type="button"
          aria-label={text}
          aria-pressed={isSelected}
          onClick={() => {
            if (nextMode === EditorMode.None) {
              store.setEditorMode(EditorMode.None);
              return;
            }
            store.resetCurrentObjectId();
            setTimeout(() => {
              store.setEditorMode(nextMode);
            }, 0);
          }}
          className={`group flex h-full w-16 cursor-pointer flex-col items-center justify-center space-y-1 rounded border-none stroke-1 px-1 transition duration-300 ease-in-out hover:-translate-y-1 hover:bg-grey-20 hover:shadow-sm hover:shadow-grey-40 ${
            isSelected ? 'bg-secondary/10' : 'bg-white'
          }`}
          {...props}
        >
          <Icon />
          <div className="hidden text-2xs transition-all group-hover:block">{text.toUpperCase()}</div>
        </button>
      </Can>
    );
  },
);

function DiscardDrawing() {
  const store = useCivilDrawStore();
  const { editorMode } = useSnapshot(store) as typeof store;
  if (editorMode.startsWith('Draw')) {
    return (
      <button
        type="button"
        onClick={() => {
          store.setEditorMode(EditorMode.None);
        }}
        className="group pointer-events-auto flex cursor-pointer items-center justify-center gap-2 rounded-full border-none bg-white px-4 py-2 text-xs text-grey-70 transition-all hover:bg-brand-dark hover:stroke-white hover:text-white"
      >
        Exit draw mode
      </button>
    );
  } else return null;
}

export default function Toolbar() {
  const [imageDialogOpen, setImageDialogOpen] = useState(false);

  return (
    <div className="pointer-events-none absolute bottom-0 mb-4 flex w-full flex-col items-center justify-end gap-2">
      <DiscardDrawing />
      <div
        className={`pointer-events-auto flex h-14 items-center justify-center gap-1 rounded bg-white p-0.5 text-secondary shadow`}
      >
        <ToolbarButton text="Polygon" mode={EditorMode.DrawPolygon} />
        <ToolbarButton text="Line" mode={EditorMode.DrawLine} />
        <ToolbarButton text="Circle" mode={EditorMode.DrawCircle} />
        <ToolbarButton text="Arrow" mode={EditorMode.DrawArrow} />
        <ToolbarButton text="Marker" mode={EditorMode.DrawMarker} />
        <ToolbarButton text="Text" mode={EditorMode.DrawTextV2} />

        <Can action={Action.EditCivilDraw}>
          <Dialog open={imageDialogOpen} onOpenChange={setImageDialogOpen}>
            <DialogTrigger asChild>
              <ToolbarButton text="Image" mode={EditorMode.DrawImage} opensPicker />
            </DialogTrigger>
            <DialogContent
              title="Upload Image"
              className="z-[110] w-auto min-w-min max-w-[75vw]"
              overlayClassName="z-[100]"
              data-testid="civildraw-image-upload-dialog"
            >
              <ImageImport closeDialog={() => setImageDialogOpen(false)} />
            </DialogContent>
          </Dialog>
        </Can>

        <Can action={Action.EditCivilDraw}>
          <Popover
            side="top"
            align="end"
            trigger={<ToolbarButton text="Stamp" mode={EditorMode.DrawStamp} opensPicker />}
          >
            {(closePopover) => <StampPicker closePopover={closePopover} />}
          </Popover>
        </Can>
      </div>
    </div>
  );
}
